// src/pages/Campaigns/SurveyGenie/CleaningRulesBuilder.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom'; // Added useNavigate
import {
    Box, Paper, Typography, Button, Grid, Card, CardContent, CardActions,
    TextField, FormControl, InputLabel, Select, MenuItem, Switch,
    FormControlLabel, IconButton, Chip, Tooltip, CircularProgress,
    Dialog, DialogTitle, DialogContent, DialogActions, Divider,
    Alert, Stack, Collapse, List, ListItem, ListItemIcon, ListItemText,
    ListItemSecondaryAction, Slider, Autocomplete
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    PlayArrow as RunIcon,
    Pause as PauseIcon,
    Rule as RuleIcon,
    Schedule as ScheduleIcon,
    Settings as SettingsIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
    CheckCircle as ActiveIcon,
    Cancel as InactiveIcon,
    ContentCopy as DuplicateIcon,
    Speed as SpeedIcon,
    Assignment as IncompleteIcon,
    TrendingUp as OutlierIcon,
    Timeline as FlatlineIcon,
    TextFields as PatternIcon,
    ArrowBack
} from '@mui/icons-material';
import axiosInstance from '../../../utils/axiosInstance';

// Rule Type Configurations
const RULE_TYPES = {
    INCOMPLETE_DETECTION: {
        label: 'Incomplete Response Detection',
        description: 'Detect responses that haven\'t answered enough questions',
        icon: <IncompleteIcon />,
        color: '#ff9800',
        defaultConditions: {
            min_completion_percentage: 80,
            required_questions: []
        },
        conditionFields: [
            {
                key: 'min_completion_percentage',
                label: 'Minimum Completion %',
                type: 'slider',
                min: 0,
                max: 100,
                step: 5
            }
        ]
    },
    DUPLICATE_DETECTION: {
        label: 'Duplicate Response Detection',
        description: 'Detect duplicate responses based on email, IP, or fingerprint',
        icon: <DuplicateIcon />,
        color: '#9c27b0',
        defaultConditions: {
            match_fields: ['email'],
            similarity_threshold: 0.95
        },
        conditionFields: [
            {
                key: 'match_fields',
                label: 'Match Fields',
                type: 'multiselect',
                options: ['email', 'ip_address', 'device_fingerprint', 'answers']
            },
            {
                key: 'similarity_threshold',
                label: 'Similarity Threshold',
                type: 'slider',
                min: 0.5,
                max: 1,
                step: 0.05
            }
        ]
    },
    SPEEDER_DETECTION: {
        label: 'Speeder Detection',
        description: 'Detect responses completed too quickly',
        icon: <SpeedIcon />,
        color: '#e91e63',
        defaultConditions: {
            min_seconds_per_question: 2,
            min_total_seconds: 60
        },
        conditionFields: [
            {
                key: 'min_seconds_per_question',
                label: 'Min Seconds Per Question',
                type: 'slider',
                min: 1,
                max: 10,
                step: 1
            },
            {
                key: 'min_total_seconds',
                label: 'Min Total Seconds',
                type: 'number',
                min: 10,
                max: 600
            }
        ]
    },
    OUTLIER_DETECTION: {
        label: 'Outlier Detection',
        description: 'Detect statistically unusual numeric answers',
        icon: <OutlierIcon />,
        color: '#f44336',
        defaultConditions: {
            method: 'zscore',
            threshold: 3,
            target_questions: []
        },
        conditionFields: [
            {
                key: 'method',
                label: 'Detection Method',
                type: 'select',
                options: [
                    { value: 'zscore', label: 'Z-Score' },
                    { value: 'iqr', label: 'IQR (Interquartile Range)' },
                    { value: 'percentile', label: 'Percentile' }
                ]
            },
            {
                key: 'threshold',
                label: 'Threshold (Standard Deviations)',
                type: 'slider',
                min: 1,
                max: 5,
                step: 0.5
            }
        ]
    },
    FLATLINE_DETECTION: {
        label: 'Flatline Pattern Detection',
        description: 'Detect same answer for consecutive rating questions',
        icon: <FlatlineIcon />,
        color: '#673ab7',
        defaultConditions: {
            min_consecutive: 5,
            question_types: ['RATING_SCALE', 'LIKERT_SCALE']
        },
        conditionFields: [
            {
                key: 'min_consecutive',
                label: 'Min Consecutive Same Answers',
                type: 'slider',
                min: 3,
                max: 10,
                step: 1
            }
        ]
    },
    PATTERN_VALIDATION: {
        label: 'Pattern Validation',
        description: 'Validate text answers against regex patterns',
        icon: <PatternIcon />,
        color: '#3f51b5',
        defaultConditions: {
            question_id: null,
            pattern: '',
            pattern_name: 'email'
        },
        conditionFields: [
            {
                key: 'pattern_name',
                label: 'Pattern Type',
                type: 'select',
                options: [
                    { value: 'email', label: 'Email Address' },
                    { value: 'phone', label: 'Phone Number' },
                    { value: 'url', label: 'URL' },
                    { value: 'zip_code', label: 'ZIP Code' },
                    { value: 'custom', label: 'Custom Regex' }
                ]
            },
            {
                key: 'pattern',
                label: 'Custom Pattern (Regex)',
                type: 'text',
                showIf: { pattern_name: 'custom' }
            }
        ]
    },
    GIBBERISH_DETECTION: {
        label: 'Gibberish Detection',
        description: 'Detect nonsense or keyboard-mash text responses',
        icon: <PatternIcon />,
        color: '#795548',
        defaultConditions: {
            min_text_length: 10,
            sensitivity: 0.5
        },
        conditionFields: [
            {
                key: 'min_text_length',
                label: 'Min Text Length to Check',
                type: 'number',
                min: 5,
                max: 100
            },
            {
                key: 'sensitivity',
                label: 'Detection Sensitivity',
                type: 'slider',
                min: 0.1,
                max: 1,
                step: 0.1
            }
        ]
    }
};

const ACTION_TYPES = [
    { value: 'FLAG_FOR_REVIEW', label: 'Flag for Review', description: 'Mark for manual review' },
    { value: 'AUTO_ARCHIVE', label: 'Auto Archive', description: 'Automatically archive the response' },
    { value: 'AUTO_DELETE', label: 'Auto Delete', description: 'Automatically delete the response' },
    { value: 'MARK_INVALID', label: 'Mark Invalid', description: 'Mark response as invalid' },
    { value: 'PROMPT_COMPLETION', label: 'Prompt Completion', description: 'Request user to complete' }
];


const RuleCard = ({ rule, onEdit, onDelete, onToggle, onRun }) => {
    const [expanded, setExpanded] = useState(false);
    const ruleConfig = RULE_TYPES[rule.rule_type] || {};

    return (
        <Card 
            sx={{ 
                mb: 2,
                borderLeft: `4px solid ${ruleConfig.color || '#1976d2'}`,
                opacity: rule.is_active ? 1 : 0.6
            }}
        >
            <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Box sx={{ 
                        p: 1, 
                        borderRadius: 1, 
                        bgcolor: `${ruleConfig.color || '#1976d2'}15`,
                        color: ruleConfig.color || '#1976d2'
                    }}>
                        {ruleConfig.icon || <RuleIcon />}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="h6">{rule.name}</Typography>
                            {rule.is_active ? (
                                <Chip size="small" label="Active" color="success" />
                            ) : (
                                <Chip size="small" label="Inactive" color="default" />
                            )}
                            {rule.is_global && (
                                <Chip size="small" label="Global" variant="outlined" />
                            )}
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                            {rule.description || ruleConfig.description}
                        </Typography>
                        
                        <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Chip 
                                size="small" 
                                variant="outlined"
                                label={ruleConfig.label || rule.rule_type}
                            />
                            <Chip 
                                size="small" 
                                variant="outlined"
                                label={`Action: ${ACTION_TYPES.find(a => a.value === rule.action_type)?.label || rule.action_type}`}
                            />
                            {rule.run_on_submission && (
                                <Chip size="small" label="On Submit" color="info" variant="outlined" />
                            )}
                            {rule.run_scheduled && (
                                <Chip 
                                    size="small" 
                                    icon={<ScheduleIcon />} 
                                    label="Scheduled" 
                                    color="secondary" 
                                    variant="outlined" 
                                />
                            )}
                        </Box>
                        
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            Triggered {rule.times_triggered || 0} times
                            {rule.last_triggered_at && ` • Last: ${new Date(rule.last_triggered_at).toLocaleDateString()}`}
                        </Typography>
                    </Box>
                    <IconButton onClick={() => setExpanded(!expanded)}>
                        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                </Box>

                <Collapse in={expanded}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" gutterBottom>Conditions</Typography>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
                        <pre style={{ margin: 0, fontSize: 12, overflow: 'auto' }}>
                            {JSON.stringify(rule.conditions, null, 2)}
                        </pre>
                    </Paper>
                </Collapse>
            </CardContent>
            <CardActions sx={{ px: 2, pb: 2 }}>
                <Button 
                    size="small" 
                    startIcon={<RunIcon />}
                    onClick={() => onRun(rule)}
                >
                    Run Now
                </Button>
                <Button 
                    size="small" 
                    startIcon={<EditIcon />}
                    onClick={() => onEdit(rule)}
                >
                    Edit
                </Button>
                <Tooltip title={rule.is_active ? 'Deactivate' : 'Activate'}>
                    <IconButton size="small" onClick={() => onToggle(rule)}>
                        {rule.is_active ? <PauseIcon /> : <RunIcon />}
                    </IconButton>
                </Tooltip>
                <Box sx={{ flex: 1 }} />
                <IconButton size="small" color="error" onClick={() => onDelete(rule)}>
                    <DeleteIcon />
                </IconButton>
            </CardActions>
        </Card>
    );
};


const ConditionField = ({ field, value, onChange, fullConfig }) => {
    // Check if field should be shown based on conditions
    if (field.showIf) {
        const conditionMet = Object.entries(field.showIf).every(
            ([key, val]) => fullConfig[key] === val
        );
        if (!conditionMet) return null;
    }

    switch (field.type) {
        case 'slider':
            return (
                <Box sx={{ px: 1 }}>
                    <Typography variant="body2" gutterBottom>
                        {field.label}: {value}
                    </Typography>
                    <Slider
                        value={value || field.min}
                        onChange={(e, v) => onChange(field.key, v)}
                        min={field.min}
                        max={field.max}
                        step={field.step}
                        marks
                        valueLabelDisplay="auto"
                    />
                </Box>
            );

        case 'number':
            return (
                <TextField
                    fullWidth
                    type="number"
                    label={field.label}
                    value={value || ''}
                    onChange={(e) => onChange(field.key, parseFloat(e.target.value))}
                    inputProps={{ min: field.min, max: field.max }}
                    size="small"
                />
            );

        case 'text':
            return (
                <TextField
                    fullWidth
                    label={field.label}
                    value={value || ''}
                    onChange={(e) => onChange(field.key, e.target.value)}
                    size="small"
                    placeholder={field.placeholder}
                />
            );

        case 'select':
            return (
                <FormControl fullWidth size="small">
                    <InputLabel>{field.label}</InputLabel>
                    <Select
                        value={value || ''}
                        label={field.label}
                        onChange={(e) => onChange(field.key, e.target.value)}
                    >
                        {field.options.map((opt) => (
                            <MenuItem key={opt.value || opt} value={opt.value || opt}>
                                {opt.label || opt}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            );

        case 'multiselect':
            return (
                <Autocomplete
                    multiple
                    options={field.options}
                    value={value || []}
                    onChange={(e, newValue) => onChange(field.key, newValue)}
                    renderInput={(params) => (
                        <TextField {...params} label={field.label} size="small" />
                    )}
                    renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                            <Chip
                                size="small"
                                label={option}
                                {...getTagProps({ index })}
                            />
                        ))
                    }
                />
            );

        default:
            return null;
    }
};

// ... [RuleEditorDialog component remains exactly the same] ...
const RuleEditorDialog = ({ open, onClose, rule, onSave, surveyId }) => {
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        rule_type: 'INCOMPLETE_DETECTION',
        conditions: {},
        action_type: 'FLAG_FOR_REVIEW',
        action_config: {},
        priority: 0,
        is_active: true,
        is_global: false,
        run_on_submission: true,
        run_scheduled: false,
        schedule_cron: ''
    });

    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (rule) {
            setFormData({
                ...formData,
                ...rule,
                conditions: rule.conditions || {}
            });
        } else {
            // Reset to defaults
            const defaultRuleType = 'INCOMPLETE_DETECTION';
            setFormData({
                name: '',
                description: '',
                rule_type: defaultRuleType,
                conditions: RULE_TYPES[defaultRuleType]?.defaultConditions || {},
                action_type: 'FLAG_FOR_REVIEW',
                action_config: {},
                priority: 0,
                is_active: true,
                is_global: false,
                run_on_submission: true,
                run_scheduled: false,
                schedule_cron: ''
            });
        }
    }, [rule, open]);

    const handleRuleTypeChange = (ruleType) => {
        setFormData({
            ...formData,
            rule_type: ruleType,
            conditions: RULE_TYPES[ruleType]?.defaultConditions || {}
        });
    };

    const handleConditionChange = (key, value) => {
        setFormData({
            ...formData,
            conditions: {
                ...formData.conditions,
                [key]: value
            }
        });
    };

    const handleSave = async () => {
        if (!formData.name.trim()) {
            alert('Rule name is required');
            return;
        }

        setSaving(true);
        try {
            if (rule?.id) {
                // Update existing rule
                await axiosInstance.put('/api/surveys/cleaning/rules', formData, {
                    params: { rule_id: rule.id }
                });
            } else {
                // Create new rule
                await axiosInstance.post('/api/surveys/cleaning/rules', formData, {
                    params: { survey_id: surveyId }
                });
            }
            onSave();
            onClose();
        } catch (error) {
            console.error('Error saving rule:', error);
            alert('Failed to save rule');
        } finally {
            setSaving(false);
        }
    };

    const ruleConfig = RULE_TYPES[formData.rule_type] || {};

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                {rule ? 'Edit Cleaning Rule' : 'Create Cleaning Rule'}
            </DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={3}>
                    {/* Basic Info */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Rule Name"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            required
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Description"
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            multiline
                            rows={2}
                        />
                    </Grid>

                    {/* Rule Type */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom>Rule Type</Typography>
                        <Grid container spacing={1}>
                            {Object.entries(RULE_TYPES).map(([key, config]) => (
                                <Grid item xs={6} md={4} key={key}>
                                    <Card
                                        variant={formData.rule_type === key ? 'elevation' : 'outlined'}
                                        sx={{
                                            cursor: 'pointer',
                                            borderColor: formData.rule_type === key ? config.color : undefined,
                                            borderWidth: formData.rule_type === key ? 2 : 1,
                                            transition: 'all 0.2s'
                                        }}
                                        onClick={() => handleRuleTypeChange(key)}
                                    >
                                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Box sx={{ color: config.color }}>{config.icon}</Box>
                                                <Typography variant="body2" fontWeight={500}>
                                                    {config.label.replace(' Detection', '')}
                                                </Typography>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>

                    {/* Conditions */}
                    <Grid item xs={12}>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            {ruleConfig.description}
                        </Alert>
                        <Typography variant="subtitle2" gutterBottom>Conditions</Typography>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Stack spacing={2}>
                                {ruleConfig.conditionFields?.map((field) => (
                                    <ConditionField
                                        key={field.key}
                                        field={field}
                                        value={formData.conditions[field.key]}
                                        onChange={handleConditionChange}
                                        fullConfig={formData.conditions}
                                    />
                                ))}
                            </Stack>
                        </Paper>
                    </Grid>

                    {/* Action */}
                    <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                            <InputLabel>Action When Triggered</InputLabel>
                            <Select
                                value={formData.action_type}
                                label="Action When Triggered"
                                onChange={(e) => setFormData({ ...formData, action_type: e.target.value })}
                            >
                                {ACTION_TYPES.map((action) => (
                                    <MenuItem key={action.value} value={action.value}>
                                        <Box>
                                            <Typography>{action.label}</Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {action.description}
                                            </Typography>
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            type="number"
                            label="Priority"
                            value={formData.priority}
                            onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
                            helperText="Higher priority rules run first"
                        />
                    </Grid>

                    {/* Execution Settings */}
                    <Grid item xs={12}>
                        <Typography variant="subtitle2" gutterBottom>Execution Settings</Typography>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={formData.is_active}
                                                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                            />
                                        }
                                        label="Rule is Active"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={formData.run_on_submission}
                                                onChange={(e) => setFormData({ ...formData, run_on_submission: e.target.checked })}
                                            />
                                        }
                                        label="Run on Response Submission"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={formData.run_scheduled}
                                                onChange={(e) => setFormData({ ...formData, run_scheduled: e.target.checked })}
                                            />
                                        }
                                        label="Run on Schedule"
                                    />
                                </Grid>
                                {!formData.survey_id && (
                                    <Grid item xs={12} sm={6}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={formData.is_global}
                                                    onChange={(e) => setFormData({ ...formData, is_global: e.target.checked })}
                                                />
                                            }
                                            label="Apply to All Surveys"
                                        />
                                    </Grid>
                                )}
                            </Grid>

                            {formData.run_scheduled && (
                                <TextField
                                    fullWidth
                                    label="Schedule (Cron Expression)"
                                    value={formData.schedule_cron}
                                    onChange={(e) => setFormData({ ...formData, schedule_cron: e.target.value })}
                                    placeholder="0 0 * * * (daily at midnight)"
                                    sx={{ mt: 2 }}
                                    helperText="Example: '0 0 * * *' for daily at midnight"
                                />
                            )}
                        </Paper>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    disabled={saving}
                    startIcon={saving && <CircularProgress size={20} />}
                >
                    {rule ? 'Update Rule' : 'Create Rule'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// Main Component
const CleaningRulesBuilder = () => {
    const { chatbotId, surveyId } = useParams();
    const navigate = useNavigate(); // Hook for navigation
    
    const [rules, setRules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editorOpen, setEditorOpen] = useState(false);
    const [editingRule, setEditingRule] = useState(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [ruleToDelete, setRuleToDelete] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const fetchRules = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.get('/api/surveys/cleaning/rules', {
                params: { survey_id: surveyId }
            });
            if (response.data.success) {
                setRules(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching rules:', error);
        } finally {
            setLoading(false);
        }
    }, [surveyId]);

    useEffect(() => {
        fetchRules();
    }, [fetchRules]);

    const handleEdit = (rule) => {
        setEditingRule(rule);
        setEditorOpen(true);
    };

    const handleCreate = () => {
        setEditingRule(null);
        setEditorOpen(true);
    };

    const handleDelete = async () => {
        if (!ruleToDelete) return;
        try {
            await axiosInstance.delete('/api/surveys/cleaning/rules', {
                params: { rule_id: ruleToDelete.id }
            });
            setSnackbar({ open: true, message: 'Rule deleted', severity: 'success' });
            fetchRules();
        } catch (error) {
            setSnackbar({ open: true, message: 'Failed to delete rule', severity: 'error' });
        } finally {
            setDeleteConfirmOpen(false);
            setRuleToDelete(null);
        }
    };

    const handleToggle = async (rule) => {
        try {
            await axiosInstance.put('/api/surveys/cleaning/rules', 
                { is_active: !rule.is_active },
                { params: { rule_id: rule.id } }
            );
            fetchRules();
        } catch (error) {
            setSnackbar({ open: true, message: 'Failed to update rule', severity: 'error' });
        }
    };

    const handleRun = async (rule) => {
        // This would trigger a detection run using this specific rule
        setSnackbar({ open: true, message: 'Rule execution started', severity: 'info' });
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, mt: 10 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3, mt: 10 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/surveys`)}>
                        <ArrowBack />
                    </IconButton>
                    <Box>
                        <Typography variant="h6">
                            <RuleIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                            Cleaning Rules
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Automate data quality checks and cleaning actions
                        </Typography>
                    </Box>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleCreate}
                >
                    Create Rule
                </Button>
            </Box>

            {/* Rules List */}
            {rules.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <RuleIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" gutterBottom>No Rules Configured</Typography>
                    <Typography color="text.secondary" sx={{ mb: 3 }}>
                        Create automated rules to detect and handle data quality issues
                    </Typography>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
                        Create Your First Rule
                    </Button>
                </Paper>
            ) : (
                <Box>
                    {rules.map((rule) => (
                        <RuleCard
                            key={rule.id}
                            rule={rule}
                            onEdit={handleEdit}
                            onDelete={(r) => { setRuleToDelete(r); setDeleteConfirmOpen(true); }}
                            onToggle={handleToggle}
                            onRun={handleRun}
                        />
                    ))}
                </Box>
            )}

            {/* Rule Editor */}
            <RuleEditorDialog
                open={editorOpen}
                onClose={() => setEditorOpen(false)}
                rule={editingRule}
                onSave={fetchRules}
                surveyId={surveyId}
            />

            {/* Delete Confirmation */}
            <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
                <DialogTitle>Delete Rule</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete the rule "{ruleToDelete?.name}"?
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={handleDelete}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CleaningRulesBuilder;